import React, { useEffect, useRef, useState } from "react";
import { Form, Row, Container, Col, Button } from "react-bootstrap";
import { Formik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import Loading from "../../components/general/Loading";
import { SubTemplate } from "../../components/general/SubTemplate";
import { FaCheck, FaRegCheckCircle, FaRegSave, FaTimes } from "react-icons/fa";
import { handleError, isNotBusinessError } from "../../utils/handleError";
import { FormSideNavBar } from "../../components/general/FormSideNavBar";
import { PriorSupportPaymentRequestStep2 } from "../../components/priorSupportPaymentRequest/PriorSupportPaymentRequestStep2";
import { PriorSupportPaymentRequestStep3 } from "../../components/priorSupportPaymentRequest/PriorSupportPaymentRequestStep3";
import { PriorSupportPaymentRequestStep4 } from "../../components/priorSupportPaymentRequest/PriorSupportPaymentRequestStep4";
import { SemestralReportFormStep1 } from "../../components/semestralReport/form/SemestralReportFormStep1";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ConfirmationAuthenticationModal } from "../../components/general/ConfirmationAuthenticationModal";
import { AlertError } from "../../components/bootstrap/AlertError";
import { SuccessToast } from "../../components/general/SuccessToast";
import { ProcessState } from "../../models/ProcessState";
import { getEntityGetList } from "../../rest/entity";
import { getHumanResourceByExternalId } from "../../rest/humanResource";
import { isIEFPUser } from "../../authentication/authenticationHelper";
import { createCustomErrorMessage } from "../../hooks/errorMessage";
import {
  getPriorSupportPaymentRequest,
  getPriorSupportPaymentRequestByProcessExternalId,
  listPriorSupportPaymentRequestDocuments,
  savePriorSupportPaymentRequest,
  withdrawPriorSupportPaymentRequest,
  annulPriorSupportPaymentRequest,
} from "../../rest/priorSupportPaymentRequest";
import { getEntitySupportedApprovedCandidatureProjectInfo } from "../../rest/priorSupport";
import * as yup from "yup";
import { getParentProcessId } from "../../rest/payableProcess";
import { PayableProcessButton } from "../../components/payableProcess/PayableProcessButton";

const validationSchema = yup.object().shape({
  identificationEa: yup.object().shape({
    technicalTeamManagerExternalId: yup
      .string()
      .test(
        "isGETValid",
        <FormattedMessage id="errors.annualComplementaryActivitiesPlan.GET" />,
        (value) => value !== "-1"
      ),
  }),

  observationsEa: yup
    .string()
    .required(<FormattedMessage id="errors.fieldRequiredText" />),

  paymentTechnicalAnalysis: yup.object().shape({
    observations: yup
      .string()
      .required(<FormattedMessage id="errors.fieldRequiredText" />),
    technicianName: yup
      .string()
      .required(<FormattedMessage id="errors.fieldRequiredText" />),
    directorName: yup
      .string()
      .required(<FormattedMessage id="errors.fieldRequiredText" />),
  }),

  decisionDispatchNumber: yup
    .string()
    .required(<FormattedMessage id="errors.fieldRequiredText" />),

  decisionDispatchDate: yup
    .date()
    .nullable()
    .required(<FormattedMessage id="errors.fieldRequiredText" />),
});

export function PriorSupportPaymentRequest() {
  const isIEFP = isIEFPUser();

  //for both iefp and entity users. In case of iefp, it is the processExternalId. Also, if it got here without the externalId, then its value is undefined
  const { externalId: externalId } = useParams();

  const [loading, setLoading] = useState(true);
  const [submited, setSubmited] = useState(false);
  const [annulled, setAnnulled] = useState(false);

  //errors
  const [error, setError] = useState(null);
  const [navErrors, setNavErrors] = useState([]);

  const [formStep, setFormStep] = useState(0);

  const [formikInitialValues, setFormikInitialValues] = useState(null);

  const [showSaveToast, setShowSaveToast] = useState(false);

  //modals
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAnnulModal, setShowAnnulModal] = useState(false);

  const [getList, setGetList] = useState([]);

  const requiredIEFPDocuments = ["DECISION_DISPATCH"];
  const [documents, setDocuments] = useState([]);

  const [candidatureProjectInfo, setCandidatureProjectInfo] = useState(null);

  const [printing, setPrinting] = useState(false);

  const navigate = useNavigate();
  const intl = useIntl();

  const formikPropsRef = useRef(null);

  const additionalTabsIds = ["priorSupportPaymentRequest.sideMenu.analysis"];

  const [tabsIds, setTabsIds] = useState([
    "priorSupportPaymentRequest.sideMenu.identificationEA",
    "priorSupportPaymentRequest.sideMenu.supportProjectIdentification",
    "priorSupportPaymentRequest.sideMenu.paymentRequest",
  ]);

  async function fetchData() {
    try {
      let [{ data: priorSupportPaymentRequest }] = await Promise.all([
        isIEFP
          ? await getPriorSupportPaymentRequestByProcessExternalId(externalId)
          : await getPriorSupportPaymentRequest(externalId),
      ]);

      let [{ data: entityGetList }] = await Promise.all([
        isIEFP
          ? { data: [] }
          : await getEntityGetList({
              all:
                priorSupportPaymentRequest.processState !== ProcessState.DRAFT,
            }),
      ]);

      if (priorSupportPaymentRequest.candidatureProcessNumber) {
        const { data: projectInfo } =
          await getEntitySupportedApprovedCandidatureProjectInfo(
            priorSupportPaymentRequest.candidatureProcessNumber,
            externalId
          );
        setCandidatureProjectInfo(projectInfo);
      }

      if (isIEFP) {
        const { data: get } = await getHumanResourceByExternalId(
          priorSupportPaymentRequest.identificationEa
            .technicalTeamManagerExternalId
        );
        entityGetList.push(get);
      }

      if (
        (isIEFP &&
          priorSupportPaymentRequest.processState !== ProcessState.DRAFT) ||
        ProcessState.finalStates().includes(
          priorSupportPaymentRequest.processState
        )
      ) {
        setTabsIds((prevStateValue) => {
          return prevStateValue.concat(additionalTabsIds);
        });
      }

      if (
        ProcessState.finalStates().includes(
          priorSupportPaymentRequest.processState
        ) ||
        isIEFP
      ) {
        let { data: processDocuments } =
          await listPriorSupportPaymentRequestDocuments(
            priorSupportPaymentRequest.externalId
          );
        let formDocumentList = requiredIEFPDocuments.map((rd) => {
          let dTypeIndex = processDocuments.findIndex(
            (d) => d.documentType === rd
          );
          if (dTypeIndex < 0) {
            //it means the document wasnt found
            return {
              externalId: "",
              documentType: rd,
              name: "",
              content: null,
              submited: false,
              error: "",
            };
          } else {
            return {
              externalId: processDocuments[dTypeIndex].externalId,
              documentType: processDocuments[dTypeIndex].documentType,
              documentPath: processDocuments[dTypeIndex].documentPath,
              name: processDocuments[dTypeIndex].name,
              content: "",
              submited: true,
              error: "",
            };
          }
        });
        setDocuments(formDocumentList);
      }

      setGetList(entityGetList);
      setFormikInitialValues(
        getFormikInitialValues(priorSupportPaymentRequest)
      );
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (printing) {
      setTimeout(() => {
        window.print();
      }, 200);
    }
  }, [printing]);

  useEffect(() => {
    const beforePrint = () => {
      setPrinting(true);
    };

    const afterPrint = () => {
      setPrinting(false);
    };

    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  function getFormikInitialValues(b7) {
    let initialValues = b7;

    if (isIEFP || ProcessState.finalStates().includes(b7.processState)) {
      initialValues.paymentTechnicalAnalysis.observations ??= "";
      initialValues.paymentTechnicalAnalysis.technicianName ??= "";
      initialValues.paymentTechnicalAnalysis.directorName ??= "";

      initialValues.decisionDispatchNumber ??= "";
      initialValues.decisionDispatchDate ??= null;
    }

    initialValues.externalId = b7.externalId;
    initialValues.processState = b7.processState;
    initialValues.identificationEa = b7.identificationEa;

    initialValues.iasAtContractCelebration ??= "";
    initialValues.iasValues ??= [];
    initialValues.observationsEa ??= "";

    return initialValues;
  }

  function scrollPageToTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  function handlePreviousStep() {
    scrollPageToTop();
    setFormStep(formStep - 1);
  }

  function handleNextStep() {
    scrollPageToTop();
    setFormStep(formStep + 1);
  }

  if (submited) {
    if (isIEFP) {
      sessionStorage.setItem(
        "priorSupportPaymentRequestDecisionSubmited",
        submited
      );
    } else {
      sessionStorage.setItem("priorSupportPaymentRequestSubmited", submited);
    }
    return isIEFP ? (
      <Navigate to="/processos" />
    ) : (
      <Navigate to="/pagamento/apoioPrevio/lista" />
    );
  }

  if (loading) {
    return <Loading />;
  }

  if (error && isNotBusinessError(error)) {
    return handleError(error);
  }

  async function handleWithdrawal(user, password, setModalError) {
    try {
      let authenticationCredentials = {
        user: user,
        password: password,
      };

      await withdrawPriorSupportPaymentRequest(
        formikPropsRef.current.values.externalId,
        authenticationCredentials
      );

      sessionStorage.setItem("priorSupportPaymentRequestWithdrawed", true);
      navigate("/pagamento/apoioPrevio/lista");
    } catch (error) {
      setModalError(error);
    }
  }

  async function handleAnnul(user, password, setModalError) {
    try {
      let authenticationCredentials = {
        user: user,
        password: password,
      };

      await annulPriorSupportPaymentRequest(
        formikPropsRef.current.values.externalId,
        authenticationCredentials
      );

      sessionStorage.setItem("priorSupportPaymentRequestAnnulled", true);
      navigate("/pagamento/apoioPrevio/lista");
    } catch (error) {
      setModalError(error);
    }
  }

  async function handleSave() {
    try {
      let { data: res } = await savePriorSupportPaymentRequest(
        formikPropsRef.current.values
      );

      let processedRes = getFormikInitialValues(res);

      formikPropsRef.current.setValues(processedRes);
      setNavErrors([]);
      formikPropsRef.current.setErrors({});

      setShowSaveToast(true);
    } catch (error) {
      setError(error);
    }
  }

  async function handleSubmit(user, password, setModalError, decisionApprove) {
    let values = formikPropsRef.current.values;
    const submissionValues = { ...values };

    submissionValues.credentials = {
      user: user,
      password: password,
    };

    if (isIEFP) {
      submissionValues.decisionApprove = decisionApprove;
    }

    let queryParams = {
      submit: true,
    };

    try {
      await savePriorSupportPaymentRequest(submissionValues, queryParams);
      setSubmited(true);
    } catch (err) {
      if (err.response.data.exception === "ValidationException") {
        handleNavError(err);
      } else {
        setModalError(err);
      }
    }
  }

  async function formValidationHandler(setShowModal) {
    let errors = await validateForm();
    setNavErrors(Array.from(errors));
    if (errors.size === 0) {
      setShowModal(true);
    }
  }

  async function validateForm() {
    let errors = new Set();

    const formikErrors = await formikPropsRef.current.validateForm();

    Object.keys(formikErrors).forEach((key) => {
      switch (key) {
        case "identificationEa":
          !isIEFP && errors.add(0);
          break;
        case "observationsEa":
          !isIEFP && errors.add(2);
          break;
        default:
          isIEFP && errors.add(3);
          break;
      }
    });

    let errorMessages = [];

    if (!isIEFP && candidatureProjectInfo === null) {
      errors.add(1);
      errorMessages.push(
        intl.formatMessage({
          id: "errors.priorSupportPaymentRequest.candidatureProcessNumber",
        })
      );
    }

    //validate that both dispatch documents have been uploaded. For the entity's user, documents = []
    documents.forEach((d) => {
      if (d.submited === false) {
        errors.add(3);
        errorMessages.push(
          intl.formatMessage({
            id: `errors.economicViabilityPaymentRequest.dispatchDocuments.${d.documentType}`,
          })
        );
      }
    });

    if (errorMessages.length > 0) {
      let message =
        errorMessages.length === 1
          ? createCustomErrorMessage(errorMessages[0])
          : createCustomErrorMessage(
              "<p>" +
                intl.formatMessage({
                  id: "errors.more",
                }) +
                "</p><p> - " +
                errorMessages.join("</p><p> - ") +
                "</p>"
            );

      setError(message);
    }
    return errors;
  }

  function handleNavError(error) {
    let errors = error.response.data.message
      .split(",")
      .map((value) => Number.parseInt(value));
    setNavErrors(errors);
  }

  function renderStep(formikProps) {
    formikPropsRef.current = formikProps;

    const readModeStep1To3 =
      formikProps.values.processState !== ProcessState.DRAFT || isIEFP;

    switch (formStep) {
      case 0:
        return (
          <SemestralReportFormStep1
            formStep={1}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handleNextStep={handleNextStep}
            entityGetList={getList}
            readMode={readModeStep1To3}
            goBackPath={isIEFP ? "/processos" : "/pagamento/apoioPrevio/lista"}
          />
        );
      case 1:
        return (
          <PriorSupportPaymentRequestStep2
            formStep={2}
            numberOfSteps={tabsIds.length}
            formikValues={formikProps.values}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            setError={setError}
            setFieldValue={formikProps.setFieldValue}
            readMode={readModeStep1To3}
            candidatureProjectInfo={candidatureProjectInfo}
            setCandidatureProjectInfo={setCandidatureProjectInfo}
          />
        );
      case 2:
        return (
          <PriorSupportPaymentRequestStep3
            formStep={3}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            readMode={readModeStep1To3}
            showNextStepButton={
              ProcessState.finalStates().includes(
                formikProps.values.processState
              ) ||
              (isIEFP && formikProps.values.processState !== ProcessState.DRAFT)
            }
            entityGetList={getList}
          />
        );
      case 3:
        return (
          <PriorSupportPaymentRequestStep4
            formStep={4}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            setFieldValue={formikProps.setFieldValue}
            handlePreviousStep={handlePreviousStep}
            readMode={ProcessState.finalStates().includes(
              formikProps.values.processState
            )}
            documents={documents}
            setDocuments={setDocuments}
            isIEFP={isIEFP}
          />
        );
      default:
        console.log("Something went wrong rendering the form step");
    }
  }
  function renderStepsForPrinting(formikProps) {
    formikPropsRef.current = formikProps;

    const readModeStep1To3 =
      formikProps.values.processState !== ProcessState.DRAFT || isIEFP;

    return (
      <>
        <SemestralReportFormStep1
          printing={printing}
          formStep={1}
          numberOfSteps={tabsIds.length}
          errors={formikProps.errors}
          formikValues={formikProps.values}
          handleChange={formikProps.handleChange}
          handleNextStep={handleNextStep}
          entityGetList={getList}
          readMode={readModeStep1To3}
          goBackPath={isIEFP ? "/processos" : "/pagamento/apoioPrevio/lista"}
        />
        <PriorSupportPaymentRequestStep2
          printing={printing}
          formStep={2}
          numberOfSteps={tabsIds.length}
          formikValues={formikProps.values}
          handlePreviousStep={handlePreviousStep}
          handleNextStep={handleNextStep}
          setError={setError}
          setFieldValue={formikProps.setFieldValue}
          readMode={readModeStep1To3}
          candidatureProjectInfo={candidatureProjectInfo}
          setCandidatureProjectInfo={setCandidatureProjectInfo}
        />
        <div className="page-break"></div>
        <PriorSupportPaymentRequestStep3
          printing={printing}
          formStep={3}
          numberOfSteps={tabsIds.length}
          errors={formikProps.errors}
          formikValues={formikProps.values}
          handleChange={formikProps.handleChange}
          handlePreviousStep={handlePreviousStep}
          handleNextStep={handleNextStep}
          readMode={readModeStep1To3}
          showNextStepButton={
            ProcessState.finalStates().includes(
              formikProps.values.processState
            ) ||
            (isIEFP && formikProps.values.processState !== ProcessState.DRAFT)
          }
          entityGetList={getList}
        />
        {tabsIds.includes("priorSupportPaymentRequest.sideMenu.analysis") && (
          <div className="page-break">
            <PriorSupportPaymentRequestStep4
              printing={printing}
              formStep={4}
              numberOfSteps={tabsIds.length}
              errors={formikProps.errors}
              formikValues={formikProps.values}
              handleChange={formikProps.handleChange}
              setFieldValue={formikProps.setFieldValue}
              handlePreviousStep={handlePreviousStep}
              readMode={ProcessState.finalStates().includes(
                formikProps.values.processState
              )}
              documents={documents}
              setDocuments={setDocuments}
              isIEFP={isIEFP}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <SubTemplate>
      <Container>
        <ConfirmationAuthenticationModal
          show={
            showWithdrawModal ||
            showSubmitModal ||
            showApproveModal ||
            showRejectModal ||
            showAnnulModal
          }
          handleClose={() =>
            showSubmitModal
              ? setShowSubmitModal(false)
              : showApproveModal
              ? setShowApproveModal(false)
              : showWithdrawModal
              ? setShowWithdrawModal(false)
              : showAnnulModal
              ? setShowAnnulModal(false)
              : setShowRejectModal(false)
          }
          submitHandler={showWithdrawModal ? handleWithdrawal : showAnnulModal ? handleAnnul : handleSubmit}
          idsPreffix={
            showApproveModal
              ? "annualComplementaryActivitiesPayment.iefp.acceptModal"
              : showRejectModal
              ? "annualComplementaryActivitiesPayment.iefp.rejectModal"
              : showWithdrawModal
              ? "annualComplementaryActivitiesPayment.withdrawModal"
              : showAnnulModal
              ? "annualComplementaryActivitiesPayment.annulModal"
              : "annualComplementaryActivitiesPayment.submitModal"
          }
          confirmButtonCompleteLabelId={
            showApproveModal
              ? "all.button.approve"
              : showRejectModal
              ? "all.button.reject"
              : showWithdrawModal
              ? "all.candidatureWithdrawal"
              : showAnnulModal
              ? "all.button.annul"
              : "all.submitButtonText"
          }
          IEFPdecisionApprove={
            showApproveModal ? true : showRejectModal ? false : undefined
          }
        />
        <Row>
          <Col md={printing ? { span: 12 } : { span: 8, offset: 2 }}>
            <h2 className="mb-1 text-secondary">
              <FormattedMessage id="priorSupportPaymentRequest.title" />
            </h2>
            <p className="mb-5 text-secondary">
              <FormattedMessage id="priorSupportPaymentRequest.subTitle" />
              <span className="badge rounded-pill bg-secondary fw-normal ms-2">
                <span className="text-uppercase fw-bold">
                  <FormattedMessage
                    id={`processState.${formikInitialValues.processState}`}
                  />{" "}
                </span>{" "}
                {formikInitialValues.submissionDate !== undefined &&
                  formikInitialValues.submissionDate !== null && (
                    <>
                      {" "}
                      ·{" "}
                      <FormattedMessage
                        id={"submitted.process.at"}
                        values={{ date: formikInitialValues.submissionDate }}
                      />{" "}
                    </>
                  )}{" "}
                {formikInitialValues.decisionDispatchDate !== undefined &&
                  formikInitialValues.decisionDispatchDate !== null && (
                    <>
                      {" "}
                      ·{" "}
                      {ProcessState.APPROVED ===
                      formikInitialValues.processState ? (
                        <FormattedMessage
                          id={"approvedIEFP.process.at"}
                          values={{
                            date: formikInitialValues.decisionDispatchDate,
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id={"rejectedIEFP.process.at"}
                          values={{
                            date: formikInitialValues.decisionDispatchDate,
                          }}
                        />
                      )}{" "}
                    </>
                  )}{" "}
              </span>
            </p>
          </Col>
        </Row>

        <Row className="mb-5">
          {!printing && (
            <Col md="2">
              <FormSideNavBar
                tabsIdsArray={tabsIds}
                formStep={formStep}
                setFormStep={setFormStep}
                navErrors={navErrors}
                setNavErrors={setNavErrors}
              />
            </Col>
          )}
          <Col md={printing ? "12" : "8"}>
            <AlertError error={error} />
            <SuccessToast
              message={intl.formatMessage({
                id: "annualComplementaryActivitiesPlan.saveToastInformation",
              })}
              show={showSaveToast}
              setShow={setShowSaveToast}
            />
            <Formik
              initialValues={formikInitialValues}
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {(formikProps) => (
                <Form onSubmit={formikProps.handleSubmit}>
                  {printing
                    ? renderStepsForPrinting(formikProps)
                    : renderStep(formikProps)}
                </Form>
              )}
            </Formik>
          </Col>
          {!printing && (
            <Col md="2">
              <Button
                className="btn btn-outline-primary d-flex align-items-center justify-content-center mb-4 w-100"
                onClick={() => setPrinting(true)}
              >
                <FormattedMessage id="all.create.pdf" />
              </Button>
              <div>
                {formikInitialValues.processState === ProcessState.APPROVED && (
                  <>
                    {isIEFP ? (
                      <PayableProcessButton processExternalId={externalId} />
                    ) : (
                      <PayableProcessButton payableExternalId={externalId} />
                    )}
                  </>
                )}
                {((isIEFP &&
                  formikInitialValues.processState ===
                    ProcessState.SUBMITTED) ||
                  (!isIEFP &&
                    formikInitialValues.processState ===
                      ProcessState.DRAFT)) && (
                  <>
                    <div>
                      {!isIEFP && (
                        <button
                          className="btn btn-outline-primary d-flex align-items-center justify-content-center mb-4 w-100"
                          type="button"
                          onClick={() => setShowWithdrawModal(true)}
                        >
                          <FormattedMessage id="all.candidatureWithdrawal" />
                        </button>
                      )}
                      <button
                        className="btn btn-outline-primary d-flex align-items-center justify-content-center mb-4 w-100"
                        type="button"
                        onClick={() => handleSave()}
                      >
                        <FaRegSave />
                        <FormattedMessage id="all.save" />
                      </button>
                      {isIEFP ? (
                        <>
                          <button
                            className="btn btn-primary d-flex align-items-center justify-content-center mb-4 w-100"
                            type="button"
                            onClick={() =>
                              formValidationHandler(setShowRejectModal)
                            }
                          >
                            <FaTimes />{" "}
                            <FormattedMessage id="all.button.reject" />
                          </button>
                          <button
                            className="btn btn-primary d-flex align-items-center justify-content-center mb-4 w-100"
                            type="button"
                            onClick={() =>
                              formValidationHandler(setShowApproveModal)
                            }
                          >
                            <FaCheck />{" "}
                            <FormattedMessage id="all.button.approve" />
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-primary d-flex align-items-center justify-content-center w-100"
                          type="button"
                          onClick={() =>
                            formValidationHandler(setShowSubmitModal)
                          }
                        >
                          <FaRegCheckCircle />
                          <FormattedMessage id="all.submitButtonText" />
                        </button>
                      )}
                    </div>
                  </>
                )}
                {(!isIEFP && formikInitialValues.processState === ProcessState.SUBMITTED) &&
                  <button
                    className="btn btn-primary d-flex align-items-center justify-content-center mb-4 w-100"
                    type="button"
                    onClick={() => setShowAnnulModal(true)}
                  >
                    <FormattedMessage id="all.button.annul" />
                  </button>
                }
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </SubTemplate>
  );
}
